import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Page from '../Page.js';
import {
  filterContentForAbsoluteLibraryUrls,
  getSingleResource,
} from '../../utils/utils.js';
import { downloadResourcePdf } from '../../utils/pdfGeneration.js';
import { Download } from 'lucide-react';
import { decodeHtml } from '../guidebooks/FeaturedGuidebooks.js';

let SingleResource = () => {
  let params = useParams();
  let [resource, setResource] = useState(null);
  let [title, setTitle] = useState(null);
  let [content, setContent] = useState(null);
  let [isLoading, setIsLoading] = useState(true);

  let [initialTitle] = useState(document.title);

  // 1. Set the title of the page to the title of the chapter.
  useEffect(() => {
    // Get the current title so we can prepend the chapter title to it.
    document.title = `${decodeHtml(title)} - ${initialTitle}`;
  }, [title]);

  useEffect(() => {
    if (params.resourceSlug) {
      // Fetch the single resource by it's slug.
      async function fetch() {
        let resource = await getSingleResource(params.resourceSlug);
        if (resource.length > 0) {
          setResource(resource[0]);
          setIsLoading(false);
        } else {
          setResource(null);
          setIsLoading(false);
        }
      }
      fetch();
    }
  }, [params.resourceSlug]);

  useEffect(() => {
    if (resource) {
      setTitle(resource.title.rendered);

      let filteredContent = filterContentForAbsoluteLibraryUrls(
        resource.content.rendered
      );

      setContent(filteredContent);
    }
  }, [resource]);

  useEffect(() => {
    if (!isLoading && !resource) {
      setTitle('Resource not found');
      setContent('Sorry, we could not find that resource.');
    }
  }, [resource, isLoading]);

  let handlePDFDownload = () => {
    downloadResourcePdf(resource);
  };

  return (
    <Page>
      <div className="flex flex-col max-w-screen-lg">
        <h1>
          <span dangerouslySetInnerHTML={{ __html: title }}></span>
        </h1>
        {resource && (
          <a
            tabIndex={0}
            className="my-6 text-sm uw-button self-start"
            onClick={handlePDFDownload}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePDFDownload();
              }
            }}
            // href={`https://sm-sharedlibrary.herokuapp.com/api/v1/resources/${params.resourceSlug}.pdf`}
          >
            <Download className="inline-block mr-2 text-white" size={16} />
            PDF
          </a>
        )}
        {resource && <InfoTable resource={resource} />}
        {resource && <ExcerptDisplay resource={resource} />}
        <div dangerouslySetInnerHTML={{ __html: content }}></div>
        {resource && <OriginalPublicationDate resource={resource} />}
        {resource && <AdditionalFiles resource={resource} />}
      </div>
    </Page>
  );
};

let OriginalPublicationDate = ({ resource }) => {
  return (
    <span className="text-sm mt-6">
      Original Publish Date:{' '}
      {new Date(resource.date).toLocaleString().split(',')[0]}
    </span>
  );
};

let InfoTable = ({ resource }) => {
  let catString = '';
  resource._embedded['wp:term'][0].map((category) => {
    catString += category.name + ', ';
  });
  // remove the final comma and space
  catString = catString.slice(0, -2);
  let date = new Date(resource.modified).toLocaleString().split(',')[0];
  // Add a space between commas if a comma exists.
  let authors = resource.acf.resource_authors.replace(/,/g, ', ');

  return (
    <table className="table-auto !border border-gray-300">
      <tbody>
        <tr>
          <td>Updated:</td>
          <td>{date}</td>
        </tr>
        {authors && (
          <tr>
            <td>{authors.split(',').length > 0 ? 'Authors:' : 'Author:'} </td>
            {/* <td>Authors:</td> */}
            <td>{authors}</td>
          </tr>
        )}
        <tr>
          <td>Categories:</td>
          <td>{catString}</td>
        </tr>
      </tbody>
    </table>
  );
};

let ExcerptDisplay = ({ resource }) => {
  return (
    <blockquote
      className="mt-4 mb-4"
      dangerouslySetInnerHTML={{ __html: resource.excerpt.rendered }}
    ></blockquote>
  );
};

let AdditionalFiles = ({ resource }) => {
  return (
    <>
      <div className="flex flex-col">
        {resource.acf.resource_files?.length > 0 && (
          <>
            <hr className="thinhr" />
            <h2 className="marbot">Additional Resources</h2>
          </>
        )}
        {resource.acf.resource_files?.length > 0 &&
          resource.acf.resource_files.map((file) => {
            return (
              <div className="w-full mt-3" key={file.resource_file_link.url}>
                <a href={file.resource_file_link.url}>
                  {/* If there's no file title, pop off the filename from the end of the url */}
                  {file.resource_file_name
                    ? file.resource_file_name
                    : file.resource_file_link.url.split('/').pop()}
                </a>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default SingleResource;
